import type { Middleware } from '@reduxjs/toolkit';

import { USER_CLEAR_TYPE } from './clearUserPersistAfterMiddleware';

export type ClearStaffViewAsPatient = () => void;

/**
 * Drops the tab's staff-viewing-as-a-patient context whenever the portal session is cleared.
 *
 * Staff sign in as a patient (`App.tsx`) and the tab then carries a view-as-patient marker and the
 * staff id in sessionStorage, outside Redux. Logout and the identity-verification sign-in drop them
 * by hand, but `user/clearUser` is dispatched from many places and most of them end a session
 * without touching either key: a failed renewal, another tab's logout, the token and
 * accessToken deep links. The next patient to sign in directly in that tab inherited a staff
 * context, so the AI chat, the Zoom channel and click-to-call treated them as staff. Handling it
 * here keeps any `clearUser` call site, present or future, from forgetting it.
 *
 * Only `user/clearUser`. A token renewal dispatches `user/setToken` for the same staff session, and
 * the marker has to survive it: a renewed token no longer carries the staff claims (LMD-939), so
 * the marker is the only thing that still says who is at the keyboard.
 *
 * Shape follows `createClearUserPersistAfterMiddleware`: run after `next(action)` so the reducer has
 * settled. The removal is synchronous, unlike the Zoom identity sweep: sessionStorage is not
 * subscribed to Redux, so there is no reducer hazard to defer around, and the context is already
 * gone when the dispatch returns, as the tokens are in `syncAuthTokensToSessionMiddleware`. The
 * staff sign-in dispatches `clearUser` first and sets the marker after the platform answers, so it
 * ends marked.
 */
export const createClearStaffViewAsPatientMiddleware = (
  clearStaffViewAsPatient: ClearStaffViewAsPatient
): Middleware => {
  return () => (next) => (action) => {
    const actionType = (action as { type: string })?.type;
    const result = next(action);

    if (actionType === USER_CLEAR_TYPE) {
      clearStaffViewAsPatient();
    }

    return result;
  };
};
